import { HomeworkGradesType } from 'types';
import {
  LmsHomeworkRosterQuery,
  LmsLessonHomeworkQuery,
  MyLessonHomeworkQuery,
} from 'core/graphql';

/** AI review status on the wire (spec 0012 §4). */
export enum HomeworkAiReviewStatus {
  PENDING = 'pending',
  FETCHING = 'fetching',
  ANALYZING = 'analyzing',
  DONE = 'done',
  FAILED = 'failed',
}

/** hw_mark wire values (`done | contains_errors | not_done`) → existing grade enum. */
const WIRE_TO_GRADE: Record<string, HomeworkGradesType> = {
  done: HomeworkGradesType.DONE,
  contains_errors: HomeworkGradesType.CONTAINS_ERRORS,
  not_done: HomeworkGradesType.NOT_DONE,
};

export const GRADE_TO_WIRE: Record<HomeworkGradesType, string> = {
  [HomeworkGradesType.DONE]: 'done',
  [HomeworkGradesType.CONTAINS_ERRORS]: 'contains_errors',
  [HomeworkGradesType.NOT_DONE]: 'not_done',
};

export const wireToGrade = (mark?: string | null): HomeworkGradesType | null =>
  (mark && WIRE_TO_GRADE[mark]) || null;

export interface IHomeworkAssignment {
  id: string;
  lessonId: string;
  /** rev.3: false = no teacher-authored assignment; the brief lives in the
   * lesson textbook. ТЗ 0026 уточнил смысл: флаг означает «есть авторский
   * ТЕКСТ», а не «строка задания существует» — иначе преподаватель,
   * заполнивший только раздел учебника, отдавал бы пустой блок. */
  hasTeacherAssignment: boolean;
  title: string;
  text: string;
  /** ТЗ 0026: «глава 7, задачи 1–4». Пусто — задание это раздел ДЗ в конце
   * учебника занятия. */
  textbookRef: string;
  /** ISO date or null — informational in v1, no submit enforcement. */
  dueDate: string | null;
  updatedAt: string | null;
}

/** Файл учебника занятия (ТЗ 0026) — `groups.StudentAttachment`. */
export interface ITextbookFile {
  id: string;
  title: string;
  fileName: string;
  isPdf: boolean;
}

const transformTextbookFiles = (
  files?:
    | MyLessonHomeworkQuery['lessonTextbookFiles']
    | LmsLessonHomeworkQuery['lessonTextbookFiles'],
): ITextbookFile[] =>
  (files || []).map((file) => ({
    id: file.id,
    title: file.title,
    fileName: file.fileName,
    isPdf: file.isPdf,
  }));

export interface IMyHomeworkSubmission {
  id: string;
  /** rev.3: a submission can carry a link AND/OR a file — render both. */
  hasGithub: boolean;
  hasFile: boolean;
  githubUrl: string | null;
  fileName: string | null;
  isGraded: boolean;
  attempt: number;
  submittedAt: string;
  /** Server nulls it until graded — no client-side gating needed. */
  hwMark: HomeworkGradesType | null;
  /** Server nulls it until graded (student-facing split type). */
  teacherComment: string;
}

export interface IMyLessonHomework {
  assignment: IHomeworkAssignment;
  mySubmission: IMyHomeworkSubmission | null;
  /** false = the server would reject a submission anyway (free-group visitor,
   * terminated agreement) — render the assignment read-only, no form. */
  canSubmit: boolean;
  /** ТЗ 0026: файлы учебника занятия — то, на что ссылается карточка ДЗ. */
  textbookFiles: ITextbookFile[];
}

export const transformMyLessonHomework = (
  data: MyLessonHomeworkQuery | undefined,
): IMyLessonHomework | null => {
  const homework = data?.myLessonHomework;
  // rev.3: the server returns an object for every homework-available lesson
  // (with hasTeacherAssignment=false when the brief is only in materials).
  // The guard stays only for the undefined-data window (loading / no field).
  if (!homework) {
    return null;
  }
  const submission = homework.mySubmission;
  return {
    assignment: {
      id: homework.id,
      lessonId: homework.lessonId,
      hasTeacherAssignment: Boolean(homework.hasTeacherAssignment),
      title: homework.title ?? '',
      text: homework.text ?? '',
      textbookRef: homework.textbookRef ?? '',
      dueDate: homework.dueDate ?? null,
      updatedAt: null,
    },
    textbookFiles: transformTextbookFiles(data?.lessonTextbookFiles),
    mySubmission: submission
      ? {
          id: submission.id,
          hasGithub: Boolean(submission.githubUrl),
          hasFile: Boolean(submission.fileName),
          githubUrl: submission.githubUrl ?? null,
          fileName: submission.fileName ?? null,
          isGraded: submission.status === 'graded',
          attempt: submission.attempt,
          submittedAt: submission.submittedAt,
          hwMark: wireToGrade(submission.hwMark),
          teacherComment: submission.teacherComment || '',
        }
      : null,
    canSubmit: Boolean(homework.canSubmit),
  };
};

/**
 * ТЗ 0026: возвращает и задание, и учебник.
 *
 * Раньше отдавалось только задание, и при `lessonHomework === null` учебник
 * терялся вместе с ним — а это как раз самый частый случай: на проде заданий
 * не заводили вовсе.
 */
export const transformLessonAssignment = (
  data: LmsLessonHomeworkQuery | undefined,
): { assignment: IHomeworkAssignment | null; textbookFiles: ITextbookFile[] } => {
  const assignment = data?.lessonHomework;
  const textbookFiles = transformTextbookFiles(data?.lessonTextbookFiles);
  if (!assignment) {
    return { assignment: null, textbookFiles };
  }
  return {
    assignment: {
      id: assignment.id,
      lessonId: assignment.lessonId,
      // «Есть авторский текст» — тот же смысл, что у студенческой ветки.
      hasTeacherAssignment: Boolean((assignment.text || '').trim()),
      title: assignment.title,
      text: assignment.text,
      textbookRef: assignment.textbookRef ?? '',
      dueDate: assignment.dueDate ?? null,
      updatedAt: assignment.updatedAt ?? null,
    },
    textbookFiles,
  };
};

export interface IHomeworkAiReview {
  status: string;
  /** pending | fetching | analyzing — spinner with a status caption. */
  isInProgress: boolean;
  isDone: boolean;
  isFailed: boolean;
  aiScore: number | null;
  aiSummary: string;
  aiJustification: string;
  suspiciousContent: boolean;
  suspicionNotes: string;
  errorMessage: string;
  analyzedAt: string | null;
  /** The verdict was computed from a PARTIAL fetch (file/char caps hit). */
  truncatedBasis: boolean;
  filesUsedCount: number | null;
  /** teacher | teacher_title+pdf_tail | pdf_tail — what fed the prompt. */
  assignmentSource: string;
}

export interface IHomeworkRosterSubmission {
  id: string;
  /** rev.3: link AND/OR file — surface both indicators on the roster row. */
  hasGithub: boolean;
  hasFile: boolean;
  githubUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  isGraded: boolean;
  attempt: number;
  submittedAt: string;
  teacherComment: string;
  gradedAt: string | null;
  gradedByName: string;
  aiReview: IHomeworkAiReview | null;
}

export interface IHomeworkRosterRow {
  studentId: string;
  studentName: string;
  hwMark: HomeworkGradesType | null;
  /** null = the student has not submitted anything. */
  submission: IHomeworkRosterSubmission | null;
}

type RosterRowSource = NonNullable<LmsHomeworkRosterQuery['homeworkRoster']>[number];
type RosterAiReviewSource = NonNullable<NonNullable<RosterRowSource['submission']>['aiReview']>;

const transformAiReview = (aiReview: RosterAiReviewSource): IHomeworkAiReview => ({
  status: aiReview.status,
  isInProgress:
    aiReview.status === HomeworkAiReviewStatus.PENDING ||
    aiReview.status === HomeworkAiReviewStatus.FETCHING ||
    aiReview.status === HomeworkAiReviewStatus.ANALYZING,
  isDone: aiReview.status === HomeworkAiReviewStatus.DONE,
  isFailed: aiReview.status === HomeworkAiReviewStatus.FAILED,
  aiScore: aiReview.aiScore ?? null,
  aiSummary: aiReview.aiSummary || '',
  aiJustification: aiReview.aiJustification || '',
  suspiciousContent: aiReview.suspiciousContent,
  suspicionNotes: aiReview.suspicionNotes || '',
  errorMessage: aiReview.errorMessage || '',
  analyzedAt: aiReview.analyzedAt ?? null,
  truncatedBasis: Boolean(aiReview.truncatedBasis),
  filesUsedCount: aiReview.filesUsedCount ?? null,
  assignmentSource: aiReview.assignmentSource ?? '',
});

export const transformHomeworkRoster = (
  data: LmsHomeworkRosterQuery | undefined,
): IHomeworkRosterRow[] =>
  (data?.homeworkRoster ?? []).map((row) => ({
    studentId: row.studentId,
    studentName: row.studentName,
    hwMark: wireToGrade(row.hwMark),
    submission: row.submission
      ? {
          id: row.submission.id,
          hasGithub: Boolean(row.submission.githubUrl),
          hasFile: Boolean(row.submission.fileName),
          githubUrl: row.submission.githubUrl ?? null,
          fileName: row.submission.fileName ?? null,
          fileSizeBytes: row.submission.fileSizeBytes ?? null,
          isGraded: row.submission.status === 'graded',
          attempt: row.submission.attempt,
          submittedAt: row.submission.submittedAt,
          teacherComment: row.submission.teacherComment || '',
          gradedAt: row.submission.gradedAt ?? null,
          gradedByName: row.submission.gradedByName || '',
          aiReview: row.submission.aiReview ? transformAiReview(row.submission.aiReview) : null,
        }
      : null,
  }));
